// src/components/Header.js - VERSION COMPLÈTE ET CORRIGÉE
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';

function Header() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const headerRef = useRef(null);
    const location = useLocation();

    // Gestion du scroll
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };

        // Effet holographique
        const handleMouseMove = (e) => {
            if (headerRef.current) {
                const rect = headerRef.current.getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width) * 100;
                const y = ((e.clientY - rect.top) / rect.height) * 100;

                headerRef.current.style.setProperty('--hologram-x', `${x}%`);
                headerRef.current.style.setProperty('--hologram-y', `${y}%`);
            }
        };

        // Effet de scan automatique
        let scanInterval;
        const startAutoScan = () => {
            scanInterval = setInterval(() => {
                if (headerRef.current && !document.querySelector('.header-tech:hover')) {
                    const x = Math.sin(Date.now() * 0.001) * 50 + 50;
                    const y = Math.cos(Date.now() * 0.001) * 50 + 50;
                    headerRef.current.style.setProperty('--hologram-x', `${x}%`);
                    headerRef.current.style.setProperty('--hologram-y', `${y}%`);
                }
            }, 100);
        };

        startAutoScan();
        window.addEventListener('scroll', handleScroll);
        window.addEventListener('mousemove', handleMouseMove);

        return () => {
            clearInterval(scanInterval);
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    // Fermer le dropdown quand on change de page
    useEffect(() => {
        setActiveDropdown(null);
        setMenuOpen(false);
    }, [location]);

    // Fermer les dropdowns quand on clique ailleurs
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.dropdown-tech')) {
                setActiveDropdown(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const toggleDropdown = (dropdownName) => {
        setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
    };

    // === DONNÉES POUR LES DROPDOWNS ===
    const servicesData = {
        categories: [
            {
                title: "Développement Web",
                icon: "💻",
                items: [
                    { name: "Applications React", link: "/services/react" },
                    { name: "Sites E-commerce", link: "/services/ecommerce" },
                    { name: "Portails Web", link: "/services/portals" }
                ]
            },
            {
                title: "Applications Mobiles",
                icon: "📱",
                items: [
                    { name: "Apps iOS", link: "/services/ios" },
                    { name: "Apps Android", link: "/services/android" },
                    { name: "Apps Cross-Platform", link: "/services/cross" }
                ]
            },
            {
                title: "Solutions Cloud",
                icon: "☁️",
                items: [
                    { name: "Architecture AWS", link: "/services/aws" },
                    { name: "DevOps & CI/CD", link: "/services/devops" },
                    { name: "Migration Cloud", link: "/services/migration" }
                ]
            },
            {
                title: "Intelligence Artificielle",
                icon: "🤖",
                items: [
                    { name: "Chatbots IA", link: "/services/chatbots" },
                    { name: "Analyse de Données", link: "/services/analytics" },
                    { name: "Computer Vision", link: "/services/vision" }
                ]
            }
        ],
        techStack: ["React", "Node.js", "Python", "AWS", "Docker", "TensorFlow"]
    };

    const formationsData = {
        categories: [
            {
                title: "Frontend",
                icon: "🎨",
                courses: [
                    { name: "React Avancé", tags: ["Hooks", "Redux"], link: "/formations/react" },
                    { name: "Vue.js 3", tags: ["Composition API"], link: "/formations/vue" },
                    { name: "Angular", tags: ["TypeScript", "RxJS"], link: "/formations/angular" }
                ]
            },
            {
                title: "Backend",
                icon: "⚙️",
                courses: [
                    { name: "Node.js Expert", tags: ["Express", "GraphQL"], link: "/formations/node" },
                    { name: "Python Django", tags: ["REST API"], link: "/formations/django" },
                    { name: "Java Spring", tags: ["Microservices"], link: "/formations/spring" }
                ]
            },
            {
                title: "Mobile",
                icon: "📱",
                courses: [
                    { name: "React Native", tags: ["iOS", "Android"], link: "/formations/react-native" },
                    { name: "Flutter", tags: ["Dart", "UI"], link: "/formations/flutter" },
                    { name: "SwiftUI", tags: ["iOS 15+"], link: "/formations/swiftui" }
                ]
            },
            {
                title: "Data Science",
                icon: "📊",
                courses: [
                    { name: "Machine Learning", tags: ["TensorFlow", "PyTorch"], link: "/formations/ml" },
                    { name: "Big Data", tags: ["Spark", "Hadoop"], link: "/formations/bigdata" },
                    { name: "Data Analytics", tags: ["Power BI", "Tableau"], link: "/formations/analytics" }
                ]
            }
        ]
    };

    // Helper functions pour les styles
    const createParticleStyle = () => ({
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        animationDelay: `${Math.random() * 4}s`
    });

    const createDotStyle = (i) => ({
        left: `${20 + (i * 15)}%`,
        animationDelay: `${i * 0.6}s`
    });

    return (
        <header
            ref={headerRef}
            className={`header-tech ${scrolled ? 'scrolled' : ''} ${menuOpen ? 'menu-open' : ''}`}
        >
            <div className="header-hologram"></div>

            <div className="header-container">
                {/* Logo */}
                <div className="logo-container">
                    <Link to="/" className="logo-link">
                        <div className="logo-hologram">
                            <div className="logo-glow"></div>
                            <img src="/ACT.png" alt="Applicodetech Logo" className="logo-image" />
                            <div className="logo-particles">
                                {[...Array(8)].map((_, i) => (
                                    <div
                                        key={i}
                                        className="logo-particle"
                                        style={createParticleStyle()}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="logo-text">
                        </div>
                    </Link>
                </div>

                {/* Menu Toggle Mobile */}
                <button
                    className={`menu-toggle-tech ${menuOpen ? 'active' : ''}`}
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label="Menu"
                >
                    <div className="toggle-glow"></div>
                    <div className="toggle-line line-1"></div>
                    <div className="toggle-line line-2"></div>
                    <div className="toggle-line line-3"></div>
                </button>

                {/* Navigation */}
                <nav className={`menu-tech ${menuOpen ? 'open' : ''}`}>
                    <div className="menu-connections">
                        <div className="connection-line"></div>
                        <div className="connection-dots">
                            {[...Array(5)].map((_, i) => (
                                <div
                                    key={i}
                                    className="connection-dot"
                                    style={createDotStyle(i)}
                                />
                            ))}
                        </div>
                    </div>

                    <ul className="menu-list">
                        {/* Accueil */}
                        <li className="menu-item">
                            <Link
                                to="/"
                                className="menu-link"
                                onClick={() => setMenuOpen(false)}
                            >
                                <span className="link-icon">🏠</span>
                                <span className="link-text">Accueil</span>
                                <span className="link-underline"></span>
                            </Link>
                        </li>

                        {/* Services Dropdown */}
                        <li className={`menu-item dropdown-tech ${activeDropdown === 'services' ? 'active' : ''}`}>
                            <button
                                className="menu-link dropdown-trigger"
                                onClick={() => toggleDropdown('services')}
                            >
                                <span className="link-icon">⚙️</span>
                                <span className="link-text">Services</span>
                                <span className="dropdown-arrow">▼</span>
                                <span className="link-underline"></span>
                            </button>

                            {/* Dropdown Services */}
                            <div className="dropdown-tech-content services-dropdown">
                                <div className="dropdown-grid">
                                    {servicesData.categories.map((category, index) => (
                                        <div key={index} className="dropdown-section">
                                            <h4 className="section-title">
                                                <span className="section-icon">{category.icon}</span>
                                                {category.title}
                                            </h4>
                                            <ul className="section-list">
                                                {category.items.map((item, itemIndex) => (
                                                    <li key={itemIndex}>
                                                        <Link
                                                            to={item.link}
                                                            onClick={() => {
                                                                setMenuOpen(false);
                                                                setActiveDropdown(null);
                                                            }}
                                                        >
                                                            {item.name}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>

                                <div className="dropdown-footer">
                                    <div className="tech-stack">
                                        {servicesData.techStack.map((tech, index) => (
                                            <span key={index} className="tech-tag">{tech}</span>
                                        ))}
                                    </div>
                                    <Link
                                        to="/all-services"
                                        className="dropdown-cta"
                                        onClick={() => {
                                            setMenuOpen(false);
                                            setActiveDropdown(null);
                                        }}
                                    >
                                        Voir tous les services
                                        <span>→</span>
                                    </Link>
                                </div>
                            </div>
                        </li>

                        {/* Formations Dropdown */}
                        <li className={`menu-item dropdown-tech ${activeDropdown === 'formations' ? 'active' : ''}`}>
                            <button
                                className="menu-link dropdown-trigger"
                                onClick={() => toggleDropdown('formations')}
                            >
                                <span className="link-icon">🎓</span>
                                <span className="link-text">Formations</span>
                                <span className="dropdown-arrow">▼</span>
                                <span className="link-underline"></span>
                            </button>

                            {/* Dropdown Formations */}
                            <div className="dropdown-tech-content formations-dropdown">
                                <div className="mega-dropdown-grid">
                                    {formationsData.categories.map((category, index) => (
                                        <div key={index} className="formation-category">
                                            <div className="category-header">
                                                <span className="category-icon">{category.icon}</span>
                                                <h4 className="category-title">{category.title}</h4>
                                            </div>
                                            <ul className="formation-list">
                                                {category.courses.map((course, courseIndex) => (
                                                    <li key={courseIndex} className="formation-item">
                                                        <Link
                                                            to={course.link}
                                                            className="formation-link"
                                                            onClick={() => {
                                                                setMenuOpen(false);
                                                                setActiveDropdown(null);
                                                            }}
                                                        >
                                                            <div className="formation-info">
                                                                <span className="formation-name">{course.name}</span>
                                                                <div className="formation-tech">
                                                                    {course.tags.map((tag, tagIndex) => (
                                                                        <span key={tagIndex} className="formation-tech-tag">
                                                                            {tag}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                            <span className="formation-arrow">→</span>
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>

                                <div className="formations-footer">
                                    <div className="formations-cta">
                                        <div className="cta-info">
                                            <span className="cta-icon">🚀</span>
                                            <div className="cta-text">
                                                <h5>Boostez votre carrière</h5>
                                                <p>Certifications reconnues internationalement</p>
                                            </div>
                                        </div>
                                        <Link
                                            to="/all-formations"
                                            className="cta-button"
                                            onClick={() => {
                                                setMenuOpen(false);
                                                setActiveDropdown(null);
                                            }}
                                        >
                                            Voir toutes les formations
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </li>

                        {/* À propos */}
                        <li className="menu-item">
                            <Link
                                to="/about"
                                className="menu-link"
                                onClick={() => setMenuOpen(false)}
                            >
                                <span className="link-icon">👥</span>
                                <span className="link-text">À propos</span>
                                <span className="link-underline"></span>
                            </Link>
                        </li>

                        {/* Blog */}
                        <li className="menu-item">
                            <Link
                                to="/blog"
                                className="menu-link"
                                onClick={() => setMenuOpen(false)}
                            >
                                <span className="link-icon">📝</span>
                                <span className="link-text">Blog</span>
                                <span className="link-underline"></span>
                            </Link>
                        </li>

                        {/* Contact */}
                        <li className="menu-item contact-item">
                            <Link
                                to="/contact"
                                className="menu-link"
                                onClick={() => setMenuOpen(false)}
                            >
                                <span className="link-icon">📱</span>
                                <span className="link-text">Contact</span>
                                <span className="link-underline"></span>
                                <span className="contact-glow"></span>
                            </Link>
                        </li>

                        {/* CTA Button */}
                        <li className="menu-item cta-item">
                            <Link
                                to="/contact"
                                className="cta-button-nav"
                                onClick={() => setMenuOpen(false)}
                            >
                                <span className="cta-glow"></span>
                                <span className="cta-icon">💬</span>
                                <span>Devis Gratuit</span>
                            </Link>
                        </li>
                    </ul>
                </nav>
            </div>
        </header>
    );
}

export default Header;